package xim.poc.game.configuration.v0.tactics

import xim.math.Vector3f
import xim.poc.ModelLook
import xim.poc.audio.BackgroundMusicResponse
import xim.poc.game.*
import xim.poc.game.actor.components.AugmentHelper
import xim.poc.game.actor.components.Inventory
import xim.poc.game.actor.components.InventoryItem
import xim.poc.game.actor.components.getInventory
import xim.poc.game.configuration.constants.armorGSprigganCoat_27902
import xim.poc.game.configuration.constants.itemFernStone_9211
import xim.poc.game.configuration.constants.itemMoonEarring_13350
import xim.poc.game.configuration.constants.itemNightEarring_13349
import xim.poc.game.configuration.standardBlurConfig
import xim.poc.game.configuration.v0.GameV0.getNumAugmentSlots
import xim.poc.game.configuration.v0.GameV0.populateItemAugments
import xim.poc.game.configuration.v0.GameV0Helpers
import xim.poc.game.configuration.v0.HelpNotifier
import xim.poc.game.configuration.v0.ItemAugmentDefinitions.getInternalQuality
import xim.poc.game.configuration.v0.ItemDefinitions
import xim.poc.game.configuration.v0.TacticsUnit
import xim.poc.game.configuration.v0.interactions.ItemId
import xim.poc.game.configuration.v0.tactics.behaviors.TacticsBehaviorController
import xim.poc.game.event.InitialActorState
import xim.poc.gl.ByteColor
import xim.poc.ui.ChatLog
import xim.poc.ui.ChatLogColor
import xim.poc.ui.ShiftJis
import xim.resource.DatId
import xim.resource.InventoryItems
import kotlin.time.Duration.Companion.seconds

class InitialEnemyUnit(
    val tacticsUnit: TacticsUnit,
    val position: CursorPosition,
    val startingTp: Int,
    val bossConfig: TacticsUnitConfig? = null,
)

sealed interface TacticsMatchConfig {
    fun getInitialEnemyUnits(): List<InitialEnemyUnit>
    fun getNextEnemyUnit(validNames: Collection<String>?): Pair<TacticsUnit, TacticsUnitConfig>?
    fun getNextBossUnit(): Pair<TacticsUnit, TacticsUnitConfig>?
    fun onEnemyDefeated(attacker: ActorState, defeated: ActorState, context: AttackContext?)
    fun isDoneSpawningEnemies(): Boolean
    fun onVictory(match: TacticsMatchInstance)
    fun getMusic(match: TacticsMatchInstance): BackgroundMusicResponse?
    fun isHoldingTurn(): Boolean = false
    fun getJobResistMultiplier(): Float = 1f
}

class TacticsChallengeMatch(val config: TacticsChallengeConfig): TacticsMatchConfig {


    override fun getInitialEnemyUnits(): List<InitialEnemyUnit> {
        return config.initialUnits.map {
            val unit = TacticsState.makeUnit(it.jobId, it.name, JobLevel(level = it.level, exp = 0))
            unit.unitRank = it.unitRank?.value ?: TacticsJobConfigs[it.jobId].minimumRank.value
            InitialEnemyUnit(unit, it.position, it.startingTp, it.unitConfig)
        }
    }

    override fun getNextEnemyUnit(validNames: Collection<String>?): Pair<TacticsUnit, TacticsUnitConfig>? {
        return null
    }

    override fun getNextBossUnit(): Pair<TacticsUnit, TacticsUnitConfig>? {
        return null
    }

    override fun onEnemyDefeated(attacker: ActorState, defeated: ActorState, context: AttackContext?) {
    }

    override fun isDoneSpawningEnemies(): Boolean {
        return true
    }

    override fun onVictory(match: TacticsMatchInstance) {
        if (config.id == tacticsChallenge_Fenrir) { upgradeItem(itemNightEarring_13349) }
        if (config.id == tacticsChallenge_Carbuncle) { upgradeItem(itemMoonEarring_13350) }

        val firstClear = TacticsState.onChallengeCleared(config.id, match)
        if (!firstClear) { return }

        TacticsChallengeConfigs.getAll()
            .filter { it.requirements.contains(config.id) }
            .filter { it.isUnlocked() }
            .forEach { ChatLog("${ShiftJis.solidSquare} Unlocked new challenge: ${ShiftJis.colorWhite}${it.displayName}${ShiftJis.colorClear}!", ChatLogColor.Info) }

        TacticsItemShop.unlockableItems[config.id]?.let {
            val itemInfo = InventoryItems[it.purchaseItem.itemId]
            ChatLog("${ShiftJis.solidSquare} ${ShiftJis.colorItem}${itemInfo.name}${ShiftJis.colorClear} is available in the shop!", ChatLogColor.Info)
        }

        val bonusText = config.bonusText ?: return
        val bonusMessage = "Unlocked: ${ShiftJis.colorAug}${bonusText}${ShiftJis.colorClear}!"

        HelpNotifier.notify(bonusMessage, 2.seconds)
        ChatLog("${ShiftJis.solidSquare} ${config.bonusDetails}", ChatLogColor.Info)
    }

    override fun getMusic(match: TacticsMatchInstance): BackgroundMusicResponse? {
        val musicId = config.musicId ?: return null
        return BackgroundMusicResponse(musicId)
    }

    override fun getJobResistMultiplier(): Float {
        return 2f/3f
    }

    private fun upgradeItem(itemId: ItemId) {
        val inventory = ActorStateManager.player().getInventory()

        val item = inventory.getByItemId(itemId).firstOrNull() ?: return
        if (item.internalQuality >= 1) { return }
        item.internalQuality = 1

        ChatLog("${ShiftJis.colorItem}${InventoryItems[itemId].name}${ShiftJis.colorClear} was upgraded to +1!", ChatLogColor.Info)
    }

}

class TacticsItemMatch(val inventoryItem: InventoryItem): TacticsMatchConfig {

    companion object {
        private const val enemiesPerBoss = 4

        fun getStartingLevel(inventoryItem: InventoryItem): Int {
            val definition = ItemDefinitions[inventoryItem]
            return definition.internalLevel
        }
    }

    private var progressState = 0
    private var regularEnemyCounter = 0
    private var instinctQueryOpen = false
    private var selectedEnemyJobs = HashSet<TacticsJobId>()

    override fun getInitialEnemyUnits(): List<InitialEnemyUnit> {
        return emptyList()
    }

    override fun getNextEnemyUnit(validNames: Collection<String>?): Pair<TacticsUnit, TacticsUnitConfig>? {
        if (regularEnemyCounter >= enemiesPerBoss) { return null }

        val enemy = makeEnemyUnit(validNames, boss = false) ?: return null
        regularEnemyCounter += 1

        val inventory = if (enemy.currentJob == tacticsJob_Spriggan) {
            Inventory().also {
                it.addItem(itemFernStone_9211, progressState * enemy.getPowerRank().value)
                it.addItem(armorGSprigganCoat_27902)
            }
        } else {
            null
        }

        return enemy to TacticsUnitConfig(
            bossUnit = false,
            xpMultiplier = 1f,
            hpMultiplier = getHpMultiplier(enemy),
            allStatMultiplier = getAllStatMultiplier(enemy),
            inventory = inventory,
        )
    }

    override fun getNextBossUnit(): Pair<TacticsUnit, TacticsUnitConfig>? {
        if (progressState >= 3) { return null }
        val bossConfig = ItemBossConfig.fromProgress(progressState) ?: return null

        val unit = makeEnemyUnit(listOf(bossConfig.name), boss = true) ?: return null

        unit.unitLevel.level += bossConfig.bonusLevel
        unit.getCurrentJobLevel().level += bossConfig.bonusLevel

        val instinctCount = progressState + 2
        unit.instincts += TacticsInstincts.getRandom(unit.getPowerRank(), instinctCount) { it + progressState * 0.05 }

        val inventory = Inventory()
        inventory.addItem(itemFernStone_9211, (progressState + 1) * (unit.getPowerRank().value - 2))

        val blurConfig = standardBlurConfig(bossConfig.auraColor)
        return unit to TacticsUnitConfig(
            bossUnit = true,
            hpMultiplier = bossConfig.maxHpMult * getHpMultiplier(unit),
            xpMultiplier = bossConfig.bonusExp,
            scale = bossConfig.modelScale,
            blurConfig = blurConfig,
            inventory = inventory,
            internalQuality = (progressState + 1),
        )
    }

    override fun onEnemyDefeated(attacker: ActorState, defeated: ActorState, context: AttackContext?) {
        openInstinctQueryIfNeeded(attacker, defeated, context)
        if (!defeated.isBossUnit()) { return }

        val newRank = when (progressState) {
            0 -> 6
            1 -> 15
            2 -> 30
            else -> return
        }

        progressState += 1

        if (progressState < 3) { regularEnemyCounter = 0 }
        selectedEnemyJobs.clear()

        val augment = inventoryItem.augments ?: return
        if (augment.rankLevel >= newRank) { return }

        augment.maxRankLevel = newRank
        augment.rankLevel = newRank

        inventoryItem.internalQuality = getInternalQuality(augment.rankLevel)

        val newAugmentSlotCount = getNumAugmentSlots(inventoryItem)
        populateItemAugments(inventoryItem, newAugmentSlotCount)

        val rankColor = AugmentHelper.getQualityColorDisplay(inventoryItem)
        val message = "Defeated the ${rankColor}${defeated.name}${ShiftJis.colorClear}! ${ShiftJis.colorItem}${inventoryItem.info().name}${ShiftJis.colorClear}${ShiftJis.rightArrow}${rankColor}R${augment.rankLevel}${ShiftJis.colorClear}!"

        AttackContext.compose(context) {
            HelpNotifier.notify(message, 2.seconds)
            ChatLog(message, ChatLogColor.Info)
        }
    }

    override fun isDoneSpawningEnemies(): Boolean {
        return progressState >= 2 && regularEnemyCounter >= enemiesPerBoss
    }

    override fun onVictory(match: TacticsMatchInstance) {
        TacticsState.onItemCleared(inventoryItem, match)
    }

    override fun getMusic(match: TacticsMatchInstance): BackgroundMusicResponse? {
        val boss = match.getUnits().firstOrNull { it.name == "Item King" } ?: return null
        return if (boss.getCursorPosition() is PanelPosition) { BackgroundMusicResponse(musicId = 144, resume = false) } else { null }
    }

    override fun isHoldingTurn(): Boolean {
        return instinctQueryOpen
    }

    private fun getAllStatMultiplier(unit: TacticsUnit): Float {
        val unitLevel = unit.unitLevel.level
        if (unitLevel >= 5) { return 1f }

        return 0.80f + 0.05f * unitLevel
    }

    private fun getHpMultiplier(unit: TacticsUnit): Float {
        return (0.7f + 0.005f * unit.unitLevel.level).coerceAtMost(1f)
    }

    private fun makeEnemyUnit(validNames: Collection<String>?, boss: Boolean): TacticsUnit? {
        val level = getLevel()
        val powerRank = getPowerRank(level)

        val jobConfig = selectEnemyJob(powerRank, boss) ?: return null
        selectedEnemyJobs += jobConfig.id

        val name = validNames?.randomOrNull() ?: tacticsUnitNames.random()

        return TacticsState.makeUnit(job = jobConfig.id, name = name, level = JobLevel(level = level, exp = 0))
            .also { it.unitRank = powerRank.value }
    }

    private fun selectEnemyJob(powerRank: StatRank, boss: Boolean): TacticsJobConfig? {
        if (!boss && progressState >= 1 && GameEngine.rollAgainst(5) && !selectedEnemyJobs.contains(tacticsJob_Spriggan)) {
            return TacticsJobConfigs[tacticsJob_Spriggan]
        }

        return TacticsJobConfigs.getAll().filter { it.itemWorldValid }
            .filter { powerRank.value >= it.spawnRank.value && powerRank.value <= it.maximumSpawnRank.value}
            .filter { !selectedEnemyJobs.contains(it.id) }
            .randomOrNull()
    }

    private fun getPowerRank(level: Int): StatRank {
        return if (level < 12) {
            StatRank.D
        } else if (level < 27) {
            StatRank.C
        } else {
            StatRank.B
        }
    }

    private fun getLevel(): Int {
        val baseLevel = getStartingLevel(inventoryItem)
        return baseLevel + progressState
    }

    private fun openInstinctQueryIfNeeded(attacker: ActorState, defeated: ActorState, context: AttackContext?) {
        val defeatedUnit = defeated.getUnit()
        if (defeatedUnit.instincts.isEmpty()) { return }

        AttackContext.compose(context) {
            val effectPromise = GameEngine.submitCreateActorState(InitialActorState(
                name = "",
                modelLook = ModelLook.fileTableIndex(0x11438),
                position = Vector3f().copyFrom(defeated.position) - Vector3f(0f, 1f, 0f),
                type = ActorType.Effect,
                targetable = false,
                popRoutines = listOf(DatId("qkla"), DatId("q005")),
            )).onReady {
                it.animationSettings.deathAnimation = DatId("q006")
            }

            instinctQueryOpen = true
            TacticsInstinctConsumeUi(
                unit = attacker.getUnit(),
                instinctOptions = defeatedUnit.instincts,
                onCompleteCallback = {
                    effectPromise.onReady(GameV0Helpers::defeatActor)
                    instinctQueryOpen = false
                    (attacker.behaviorController as? TacticsBehaviorController)?.onInstinctsChanged()
                },
            ).push()
        }
    }

}

private class ItemBossConfig private constructor(
    val name: String,
    val maxHpMult: Float,
    val modelScale: Float,
    val bonusLevel: Int,
    val bonusExp: Float,
    val auraColor: ByteColor,
) {
    companion object {
        private val itemBoss = ItemBossConfig(
            name = "Item Boss",
            maxHpMult = 1.5f,
            modelScale = 1.15f,
            bonusLevel = 1,
            bonusExp = 2f,
            auraColor = ByteColor(0x40, 0x80, 0x50, 0x30),
        )

        private val itemGeneral = ItemBossConfig(
            name = "Item General",
            maxHpMult = 1.75f,
            modelScale = 1.30f,
            bonusLevel = 1,
            bonusExp = 2.5f,
            auraColor = ByteColor(0x40, 0x50, 0x80, 0x30),
        )

        private val itemKing = ItemBossConfig(
            name = "Item King",
            maxHpMult = 2f,
            modelScale = 1.50f,
            bonusLevel = 2,
            bonusExp = 3f,
            auraColor = ByteColor(0x80, 0x60, 0x00, 0x30),
        )

        fun fromProgress(progress: Int): ItemBossConfig? {
            return when (progress) {
                0 -> itemBoss
                1 -> itemGeneral
                2 -> itemKing
                else -> null
            }
        }

    }

}